import React from 'react';
import { TouchableOpacity, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { faChevronDown, faChevronRight, faClipboardCheck, faClock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import Card from '../../../components/Card';
import Badge from '../../../components/Badge';
import MyAppText from '../../../components/MyAppText';
import CustomButton from '../../../components/CustomButton';
import { i18nKeys, translate } from '../../../services/translate';
import FontWeight from '../../../styles/fontWeights';

const MOMENT_KEYS: Record<string, i18nKeys> = {
    before_departure: 'oseMomentBeforeDeparture',
    during_service: 'oseMomentDuringService',
    after_service: 'oseMomentAfterService',
};

const STATUS_KEYS: Record<ExternalServiceOrderStatus, i18nKeys> = {
    open: 'oseOpen',
    finished: 'oseFinished',
    returned: 'oseReturned',
};

const STATUS_COLORS: Record<ExternalServiceOrderStatus, 'orange' | 'green' | 'purple'> = {
    open: 'orange',
    finished: 'green',
    returned: 'purple',
};

interface Props {
    team: AppAgentUnit;
    expanded: boolean;
    onToggle: () => void;
    onCreateOse: () => void;
    onOpenOse: (ose: AppAgentOse) => void;
}

export default function TeamCard({ team, expanded, onToggle, onCreateOse, onOpenOse }: Props) {
    const { styles, theme } = useStyles(styleSheet);

    const oses = team.unitExternalServiceOrders ?? [];
    const openOse = oses.find((o) => o.status === 'open');
    const returnedCount = oses.filter((o) => o.status === 'returned').length;
    const category = team.unitName.unitCategory;
    const generatesOse = !!category?.generateOSE;
    const hasChecklist = (category?.checklistCount ?? 0) > 0;
    const canCreate = generatesOse && hasChecklist && !openOse;

    function getCreateHint(): string | null {
        if (!generatesOse) {
            return translate('oseCategoryDoesNotGenerate');
        }

        if (!hasChecklist) {
            return translate('oseCategoryWithoutChecklist');
        }

        if (openOse) {
            return translate('oseAlreadyOpen');
        }

        return null;
    }

    const createHint = getCreateHint();

    const start = team.definitiveStart ?? team.estimatedStart;
    const finish = team.definitiveFinish ?? team.estimatedFinish;

    return (
        <Card>
            <TouchableOpacity onPress={onToggle}>
                <View style={{ flexDirection: 'row' }}>
                    <View style={{ flexDirection: 'column', flex: 1 }}>
                        <View style={styles.overlineRow}>
                            <FontAwesomeIcon
                                icon={faClipboardCheck}
                                fontSize={11}
                                color={openOse ? theme.colors.badges.orange.textColor : theme.colors.neutral['400']}
                            />
                            <MyAppText style={[styles.overlineText, !!openOse && styles.overlineTextOpen]}>
                                {translate('ose')}{openOse ? ` · ${translate('oseOpen')}` : ''}
                            </MyAppText>
                        </View>

                        <MyAppText style={styles.title} numberOfLines={1}>
                            {team.unitName.name}
                            {team.unitName.plate ? ` · ${team.unitName.plate}` : ''}
                        </MyAppText>

                        <View style={styles.timeRow}>
                            <FontAwesomeIcon icon={faClock} fontSize={14} color={theme.colors.neutral['500']} />
                            <MyAppText style={styles.timeText}>
                                {`${moment(start).format('DD/MM/YYYY HH:mm')} – ${moment(finish).format('DD/MM/YYYY HH:mm')}`}
                            </MyAppText>
                        </View>

                        <View style={styles.badgesRow}>
                            {oses.length > 0 && (
                                <Badge mode='ROUNDED' color='purple' textColor={theme.colors.badges.purple.textColor}>
                                    {`${oses.length} ${translate(oses.length === 1 ? 'ose' : 'oses')}`}
                                </Badge>
                            )}
                            {returnedCount > 0 && (
                                <Badge mode='ROUNDED' color='orange' textColor={theme.colors.badges.orange.textColor}>
                                    {`${returnedCount} ${translate(returnedCount === 1 ? 'oseReturned' : 'oseReturnedPlural')}`}
                                </Badge>
                            )}
                            {!!openOse && openOse.nonConformCount > 0 && (
                                <Badge mode='ROUNDED' color='red' textColor={theme.colors.badges.red.textColor}>
                                    {`${openOse.nonConformCount} ${translate(openOse.nonConformCount === 1 ? 'nonCompliantSingular' : 'nonCompliantPlural')}`}
                                </Badge>
                            )}
                        </View>
                    </View>

                    <View style={styles.chevron}>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            fontSize={14}
                            rotation={expanded ? 180 : undefined}
                            color={theme.colors.neutral['500']}
                        />
                    </View>
                </View>
            </TouchableOpacity>

            {expanded && (
                <View style={styles.body}>
                    {canCreate
                        ? (
                            <CustomButton
                                onPress={onCreateOse}
                                isLoading={false}
                                text={translate('newOse')}
                            />
                        )
                        : <MyAppText style={styles.hint}>{createHint}</MyAppText>
                    }

                    {canCreate && oses.length === 0 && (
                        <MyAppText style={styles.hint}>{translate('oseNoOses')}</MyAppText>
                    )}

                    {oses.map((ose) => {
                        const isOpenOse = ose.status === 'open';

                        const subtitle = [
                            ose.driver?.name,
                            isOpenOse ? translate(MOMENT_KEYS[ose.currentMoment]) : null,
                            ose.status === 'finished' && ose.finishedAt
                                ? translate('oseFinishedAt', { date: moment(ose.finishedAt).format('DD/MM/YYYY HH:mm') })
                                : null,
                        ].filter(Boolean).join(' · ');

                        const rowContent = (
                            <View style={styles.oseInfo}>
                                <MyAppText style={styles.oseDate}>
                                    {moment(ose.createdAt).format('DD/MM/YYYY HH:mm')}
                                </MyAppText>
                                {!!subtitle && (
                                    <MyAppText style={styles.timeText} numberOfLines={1}>{subtitle}</MyAppText>
                                )}
                                <View style={styles.badgesRow}>
                                    <Badge
                                        mode='ROUNDED'
                                        color={STATUS_COLORS[ose.status]}
                                        textColor={theme.colors.badges[STATUS_COLORS[ose.status]].textColor}
                                    >
                                        {translate(STATUS_KEYS[ose.status])}
                                    </Badge>
                                    {ose.nonConformCount > 0 && (
                                        <Badge mode='ROUNDED' color='red' textColor={theme.colors.badges.red.textColor}>
                                            {`${ose.nonConformCount} ${translate(ose.nonConformCount === 1 ? 'nonCompliantSingular' : 'nonCompliantPlural')}`}
                                        </Badge>
                                    )}
                                    {isOpenOse && ose.pendingCount > 0 && (
                                        <Badge mode='ROUNDED' color='orange' textColor={theme.colors.badges.orange.textColor}>
                                            {`${ose.pendingCount} ${translate(ose.pendingCount === 1 ? 'pendingSingular' : 'pendingPlural')}`}
                                        </Badge>
                                    )}
                                </View>
                            </View>
                        );

                        if (!isOpenOse) {
                            return (
                                <View key={ose.id} style={styles.oseRow}>
                                    {rowContent}
                                </View>
                            );
                        }

                        return (
                            <TouchableOpacity key={ose.id} style={styles.oseRow} onPress={() => onOpenOse(ose)}>
                                {rowContent}
                                <FontAwesomeIcon icon={faChevronRight} fontSize={12} color={theme.colors.neutral['400']} />
                            </TouchableOpacity>
                        );
                    })}
                </View>
            )}
        </Card>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    overlineRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        marginBottom: 6
    },
    overlineText: {
        fontSize: 11,
        fontWeight: FontWeight.semiBold,
        letterSpacing: 0.3,
        color: theme.colors.neutral['400'],
        textTransform: 'uppercase',
    },
    overlineTextOpen: {
        color: theme.colors.badges.orange.textColor
    },
    title: {
        fontSize: 15,
        fontWeight: FontWeight.semiBold,
        color: theme.colors.field.text,
        marginBottom: 6
    },
    timeRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6
    },
    timeText: {
        fontSize: 13,
        color: theme.colors.neutral['500']
    },
    badgesRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 6,
        marginTop: 10
    },
    chevron: {
        alignSelf: 'center',
        paddingLeft: 12
    },
    body: {
        marginTop: 14,
        paddingTop: 14,
        gap: 10,
        borderTopWidth: 1,
        borderTopColor: theme.colors.neutral['200'],
    },
    hint: {
        fontSize: 13,
        color: theme.colors.neutral['500']
    },
    oseRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 10,
        paddingHorizontal: 12,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: theme.colors.neutral['200'],
    },
    oseDate: {
        fontSize: 14,
        fontWeight: FontWeight.semiBold,
        color: theme.colors.field.text
    },
    oseInfo: {
        flex: 1,
        gap: 4
    },
}));
